import {
  AsYouType,
  type CountryCode,
  getCountries,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString
} from "libphonenumber-js"

export function formatPhoneNumber(phoneNumber: string) {
  if (phoneNumber === "NA") return "No phone number"
  let formatedNumber: any
  try {
    formatedNumber = new AsYouType().input(phoneNumber)
  } catch {
    formatedNumber = phoneNumber
  }
  return formatedNumber || phoneNumber
}

export function isDialablePhoneNumber(raw: unknown) {
  const trimmed = String(raw ?? "").replace(/[\s()-]/g, "")
  if (!trimmed.startsWith("+")) return false
  try {
    return isPossiblePhoneNumber(trimmed)
  } catch {
    return false
  }
}

const KNOWN_ISOS = new Set<string>(getCountries())

const ISO_BY_COUNTRY_NAME: Record<string, CountryCode> = (() => {
  const names = new Intl.DisplayNames(["en"], { type: "region" })
  const map: Record<string, CountryCode> = {}
  for (const iso of getCountries()) {
    const label = names.of(iso)
    if (label) map[label.toLowerCase()] = iso
  }
  return map
})()

const ISO_ALIASES: Record<string, CountryCode> = {
  usa: "US",
  "u.s.": "US",
  "u.s.a.": "US",
  "united states of america": "US",
  america: "US",
  uk: "GB",
  "u.k.": "GB",
  britain: "GB",
  "great britain": "GB",
  england: "GB",
  uae: "AE",
  "u.a.e.": "AE",
  "south korea": "KR",
  "north korea": "KP",
  russia: "RU",
  vietnam: "VN",
  czechia: "CZ",
  "czech republic": "CZ",
  turkey: "TR",
  netherlands: "NL",
  holland: "NL"
}

export function isoFromCountryName(
  value?: string | null
): CountryCode | undefined {
  const raw = String(value ?? "").trim()
  if (!raw) return undefined
  const alias = ISO_ALIASES[raw.toLowerCase()]
  if (alias) return alias
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const upper = raw.toUpperCase()
    return KNOWN_ISOS.has(upper) ? (upper as CountryCode) : undefined
  }
  return ISO_BY_COUNTRY_NAME[raw.toLowerCase()]
}

export function isoFromPhone(
  raw: unknown,
  ...countryHints: (string | null | undefined)[]
): CountryCode | undefined {
  const e164 = toE164(raw, ...countryHints)
  if (!e164) return undefined
  return parsePhoneNumberFromString(e164)?.country
}

export function hasCountryCode(raw: unknown) {
  return String(raw ?? "")
    .replace(/[^\d+]/g, "")
    .startsWith("+")
}

export function toE164(
  raw: unknown,
  ...countryHints: (string | null | undefined)[]
) {
  const digits = String(raw ?? "").replace(/[^\d+]/g, "")
  if (!digits) return null
  if (digits.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(digits)
    return parsed?.isPossible() ? parsed.number : null
  }
  for (const hint of countryHints) {
    const region = isoFromCountryName(hint)
    if (!region) continue
    const parsed = parsePhoneNumberFromString(digits, region)
    if (parsed?.isPossible()) return parsed.number
  }
  return null
}

// The same thing, with US assumed when nothing else says otherwise. A bare
// national number is dialable this way instead of being parked as uncallable,
// which is the whole point - but the assumption is only ever good enough to
// DIAL on. It must not be read back as a fact about where someone is: a +1 the
// caller invented looks exactly like a +1 they gave us, and anything deriving a
// country from the prefix would place an Indian number in America.
export function toDialableE164(
  raw: unknown,
  ...countryHints: (string | null | undefined)[]
) {
  const exact = toE164(raw, ...countryHints)
  if (exact) return exact
  const digits = String(raw ?? "").replace(/[^\d+]/g, "")
  if (!digits || digits.startsWith("+")) return null
  const asUs = parsePhoneNumberFromString(digits, "US")
  return asUs?.isPossible() ? asUs.number : null
}
